import { logger } from './logger';

/** Temporary CIAM-816 instrumentation; remove once loyalty-id recovery is verified in stage. */
export const AUTH0_LOYALTY_ID_DEBUG_PREFIX = '[CIAM-816 loyalty-id]';

export type Auth0LoyaltyIdDebugStage =
    | 'coordinator.entry'
    | 'coordinator.skipped'
    | 'coordinator.throttled'
    | 'coordinator.in_flight_join'
    | 'coordinator.reservation_written'
    | 'coordinator.reservation_lost'
    | 'coordinator.token_refresh_start'
    | 'coordinator.token_refresh_result'
    | 'coordinator.claim_check'
    | 'coordinator.resolved'
    | 'coordinator.unresolved'
    | 'coordinator.error'
    | 'coordinator.listener'
    | 'claim_sync.evaluate'
    | 'claim_sync.skip'
    | 'claim_sync.claims_read'
    | 'claim_sync.refresh_request'
    | 'claim_sync.refresh_result'
    | 'claim_sync.retry_scheduled'
    | 'claim_sync.retry_suppressed'
    | 'claim_sync.visibility'
    | 'id_token_user.claims_refresh_notify'
    | 'id_token_user.sync_start'
    | 'id_token_user.claims_read'
    | 'id_token_user.mapped'
    | 'id_token_user.unchanged'
    | 'id_token_user.cleared'
    | 'id_token_user.error'
    | 'loyalty_provider.gate'
    | 'loyalty_provider.fetch_blocked'
    | 'loyalty_provider.fetch_scheduled'
    | 'loyalty_provider.unavailable';

type LoyaltyClaimSnapshot = {
    loyaltyid: unknown;
    namespacedLoyaltyId: unknown;
    parsedLoyaltyId: number;
    hasUsableLoyaltyId: boolean;
    sub?: unknown;
    sid?: unknown;
    customerUuid?: unknown;
};

/** Normalises loyalty claim fields from raw id-token claims or mapped session user. */
export function snapshotAuth0LoyaltyClaims(claims: unknown): LoyaltyClaimSnapshot | undefined {
    if (!claims || typeof claims !== 'object') {
        return undefined;
    }

    const record = claims as Record<string, unknown>;
    const loyaltyid = record.loyaltyid;
    const namespacedLoyaltyId = record['petsmart.com/loyalty_id'];
    const raw = loyaltyid ?? namespacedLoyaltyId;
    const parsedLoyaltyId = Number(raw);

    return {
        loyaltyid,
        namespacedLoyaltyId,
        parsedLoyaltyId: Number.isFinite(parsedLoyaltyId) ? parsedLoyaltyId : Number.NaN,
        hasUsableLoyaltyId: Boolean(Number(raw)),
        sub: record.sub,
        sid: record.sid,
        customerUuid: record['petsmart.com/uuid'],
    };
}

/** Client-only structured log for tracing loyalty-id retrieval across tabs and refresh attempts. */
export function logAuth0LoyaltyIdRetrieval(
    stage: Auth0LoyaltyIdDebugStage,
    details: Record<string, unknown> = {}
): void {
    if (globalThis.window === undefined) {
        return;
    }

    const claims = details.claims;
    const payload = {
        stage,
        at: new Date().toISOString(),
        visibility: globalThis.document?.visibilityState,
        ...details,
        ...(claims === undefined ? {} : { claims: snapshotAuth0LoyaltyClaims(claims) ?? claims }),
    };

    logger.info(`${AUTH0_LOYALTY_ID_DEBUG_PREFIX} ${stage}`, payload);
}
